import { getDistance } from "geolib";

/** URL token for map sidebar distance filter. */
export const MAP_DIST_URL_KEY = "map_dist";
export const MAP_RINGS_URL_KEY = "map_rings";

export const MAP_DIST_ANY = "any";

export const MAP_DIST_OPTIONS = [
  { id: MAP_DIST_ANY, label: "Any", radiusKm: null },
  { id: "km1", label: "1 km", radiusKm: 1 },
  { id: "km3", label: "3 km", radiusKm: 3 },
  { id: "km5", label: "5 km", radiusKm: 5 },
];

const LEGACY_DIST_MAP = {
  any: MAP_DIST_ANY,
  lte3: "km3",
  "3to7": "km5",
  gt7: "km5",
  km1: "km1",
  km3: "km3",
  km5: "km5",
  km7: "km5",
};

export const MAP_DIST_DEFAULT = MAP_DIST_ANY;

export function parseMapDistParam(raw) {
  if (raw == null || String(raw).trim() === "") return MAP_DIST_DEFAULT;
  const v = String(raw).trim().toLowerCase();
  const mapped = LEGACY_DIST_MAP[v] || v;
  return MAP_DIST_OPTIONS.some((o) => o.id === mapped) ? mapped : MAP_DIST_DEFAULT;
}

export function isMapDistAny(rangeId) {
  return parseMapDistParam(rangeId) === MAP_DIST_ANY;
}

export function getMapDistanceRadiusKm(rangeId) {
  const id = parseMapDistParam(rangeId);
  const opt = MAP_DIST_OPTIONS.find((o) => o.id === id);
  return opt?.radiusKm ?? null;
}

export function parseMapRingsParam(raw) {
  if (raw == null || raw === "") return true;
  const v = String(raw).trim().toLowerCase();
  return v !== "0" && v !== "false";
}

/** Distance in km from center to listing; null if coords missing. */
export function distanceKmFromCenter(center, latLng) {
  if (!center || !latLng) return null;
  const lat = parseFloat(latLng.lat);
  const lng = parseFloat(latLng.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return getDistance(center, { latitude: lat, longitude: lng }) / 1000;
}

/** Listing within selected radius (inclusive). Any = no limit. */
export function matchesMapDistanceRange(km, rangeId) {
  if (isMapDistAny(rangeId)) return true;
  if (km == null) return false;
  const radius = getMapDistanceRadiusKm(rangeId);
  if (radius == null) return true;
  return km <= radius;
}

export function filterListingsByMapDistance(listings, center, rangeId, getLatLngFn) {
  if (!center || !Array.isArray(listings) || isMapDistAny(rangeId)) return listings;
  return listings.filter((item) => {
    const pos = getLatLngFn(item);
    const km = distanceKmFromCenter(center, pos);
    return matchesMapDistanceRange(km, rangeId);
  });
}

/** Single ring for the active distance only (hidden when Any or rings off). */
export function getMapDistanceRingRadii(showRings, rangeId) {
  if (!showRings || isMapDistAny(rangeId)) return [];
  const km = getMapDistanceRadiusKm(rangeId);
  return km != null ? [km] : [];
}

/** Active map framing radius: distance filter, or 15 km for location search. */
export function getLocationViewRadiusKm(rangeId, { nearby = false, qsRadius = null } = {}) {
  const filtered = getMapDistanceRadiusKm(rangeId);
  if (filtered != null) return filtered;
  if (nearby && qsRadius != null && String(qsRadius).trim() !== "") {
    const n = parseFloat(qsRadius);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return 15;
}

/** Radius (km) that covers all listing pins from a fixed center, for map lock / labels. */
export function getListingSpreadRadiusKm(
  center,
  listings,
  getLatLngFn,
  { minKm = 4, maxKm: maxKmCap = 28, padding = 1.15, fallbackKm = 15 } = {}
) {
  if (!center || !Array.isArray(listings) || !listings.length) return fallbackKm;
  let farthestKm = 0;
  let count = 0;
  listings.forEach((item) => {
    const km = distanceKmFromCenter(center, getLatLngFn(item));
    if (km == null) return;
    farthestKm = Math.max(farthestKm, km);
    count += 1;
  });
  if (count === 0) return fallbackKm;
  return Math.min(maxKmCap, Math.max(minKm, farthestKm * padding + 0.5));
}

/** Bounds for a circle around center — used to frame the map on the active area. */
export function getBoundsForRadiusKm(center, radiusKm, googleMaps) {
  if (!center || radiusKm == null || !googleMaps?.maps?.Circle) return null;
  const circle = new googleMaps.maps.Circle({
    center,
    radius: radiusKm * 1000,
  });
  return circle.getBounds?.() || null;
}
